import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth, db, storage } from '../firebaseConfig'; // Make sure path is correct
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  onSnapshot
} from 'firebase/firestore';
import {
  ref,
  uploadString,
  getDownloadURL
} from "firebase/storage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // This state holds the combined user data (from Auth and Firestore)
    const [currentUser, setCurrentUser] = useState(null);
    
    // This holds the list of all user profiles (for the Comments.js component)
    const [users, setUsers] = useState([]);
    
    // Loading state for ProtectedRoute
    const [loading, setLoading] = useState(true);

    // --- REAL-TIME AUTH LISTENER ---
    // This is the most important part. It listens for login/logout
    // and automatically fetches the user's profile from Firestore.
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // User is logged in, now get their profile from Firestore
                const docRef = doc(db, "users", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    // Combine auth data (uid, email) with profile data (username, etc.)
                    setCurrentUser({ ...user, ...docSnap.data() });
                } else {
                    // This case is rare (user exists in Auth but not Firestore)
                    setCurrentUser(user);
                }
            } else {
                // User is logged out
                setCurrentUser(null);
            }
            setLoading(false); // Auth check is complete
        });

        return () => unsubscribe(); // Cleanup listener on unmount
    }, []);

    // --- REAL-TIME USERS LIST LISTENER ---
    // This provides the 'users' array needed for Comments.js
    useEffect(() => {
        const usersQuery = query(collection(db, "users"));
        
        const unsubscribe = onSnapshot(usersQuery, (querySnapshot) => {
            const fetchedUsers = [];
            querySnapshot.forEach((doc) => {
                fetchedUsers.push({ ...doc.data(), id: doc.id });
            });
            setUsers(fetchedUsers);
        });

        return () => unsubscribe(); // Cleanup listener
    }, []);


    // --- REGISTER FUNCTION ---
    const register = async (username, email, password, profilePhoto, firstName, lastName) => {
        // 1. Create the user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        let photoURL = null;

        // 2. If a profile photo was provided, upload it to Firebase Storage
        if (profilePhoto) {
            const storageRef = ref(storage, `profilePhotos/${user.uid}`);
            await uploadString(storageRef, profilePhoto, 'data_url');
            photoURL = await getDownloadURL(storageRef);
        }

        // 3. Update the new user's Auth profile (for photoURL and displayName)
        await updateProfile(user, {
            displayName: username,
            photoURL: photoURL
        });

        // 4. Create the user's profile document in Firestore
        await setDoc(doc(db, "users", user.uid), {
            username: username,
            email: email,
            firstName: firstName,
            lastName: lastName,
            profilePhoto: photoURL,
            uid: user.uid
        });

        // The onAuthStateChanged listener will automatically set the currentUser
    };

    // --- LOGIN FUNCTION ---
    const login = (email, password) => {
        // This will trigger the onAuthStateChanged listener
        return signInWithEmailAndPassword(auth, email, password);
    };

    // --- LOGOUT FUNCTION ---
    const logout = () => {
        // This will trigger the onAuthStateChanged listener
        return signOut(auth);
    };

    // --- UPDATE PHOTO FUNCTION ---
    const updateUserPhoto = async (newPhoto) => {
        if (!currentUser) throw new Error("No user is logged in.");

        setLoading(true);
        try {
            // 1. Upload new photo to storage
            const storageRef = ref(storage, `profilePhotos/${currentUser.uid}`);
            await uploadString(storageRef, newPhoto, 'data_url');
            const downloadURL = await getDownloadURL(storageRef);

            // 2. Update the Auth profile
            await updateProfile(auth.currentUser, { photoURL: downloadURL });

            // 3. Update the Firestore profile document
            const docRef = doc(db, "users", currentUser.uid);
            await updateDoc(docRef, { profilePhoto: downloadURL });

            // 4. Manually update state for instant UI change
            setCurrentUser(prevUser => ({ ...prevUser, profilePhoto: downloadURL }));
        } catch (error) {
            console.error("Error updating photo:", error);
            throw error; // Re-throw to be caught by the component
        } finally {
            setLoading(false);
        }
    };

    const isAuthenticated = !!currentUser;

    const value = {
        isAuthenticated,
        currentUser,
        users, // This is the list of ALL users for the comment section
        loading, // For ProtectedRoute
        register,
        login,
        logout,
        updateUserPhoto
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};